import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ModuleProgress, LearningPathProgress } from 'src/types/learning';

interface LearningState {
  currentPath: string | null;
  currentModule: string | null;
  pathProgress: Record<string, LearningPathProgress>;
  moduleProgress: Record<string, ModuleProgress>;
  recommendedPaths: string[];
}

const initialState: LearningState = {
  currentPath: null,
  currentModule: null,
  pathProgress: {},
  moduleProgress: {},
  recommendedPaths: [],
};

// Load from localStorage
const loadLearningState = (): Partial<LearningState> => {
  try {
    const saved = localStorage.getItem('telix_learning_state');
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        currentPath: parsed.currentPath || null,
        currentModule: parsed.currentModule || null,
        pathProgress: parsed.pathProgress || {},
        moduleProgress: parsed.moduleProgress || {},
        recommendedPaths: parsed.recommendedPaths || [],
      };
    }
  } catch (error) {
    console.error('Failed to load learning state:', error);
  }
  return {};
};

const savedState = loadLearningState();

const learningSlice = createSlice({
  name: 'learning',
  initialState: { ...initialState, ...savedState },
  reducers: {
    setCurrentPath: (state, action: PayloadAction<string | null>) => {
      state.currentPath = action.payload;
      saveToLocalStorage(state);
    },
    setCurrentModule: (state, action: PayloadAction<string | null>) => {
      state.currentModule = action.payload;
      saveToLocalStorage(state);
    },
    updatePathProgress: (state, action: PayloadAction<LearningPathProgress>) => {
      state.pathProgress[action.payload.pathId] = action.payload;
      saveToLocalStorage(state);
    },
    updateModuleProgress: (state, action: PayloadAction<ModuleProgress>) => {
      state.moduleProgress[action.payload.moduleId] = action.payload;
      saveToLocalStorage(state);
    },
    setRecommendedPaths: (state, action: PayloadAction<string[]>) => {
      state.recommendedPaths = action.payload;
      saveToLocalStorage(state);
    },
    completeLesson: (
      state,
      action: PayloadAction<{ moduleId: string; lessonId: string }>
    ) => {
      const { moduleId, lessonId } = action.payload;
      if (!state.moduleProgress[moduleId]) {
        state.moduleProgress[moduleId] = {
          moduleId,
          userId: 'current-user', // TODO: Get from auth
          lessonsCompleted: [],
          quizzesPassed: [],
          projectsCompleted: [],
          currentLesson: lessonId,
          startedAt: new Date(),
          lastAccessedAt: new Date(),
          score: 0,
        };
      }
      const progress = state.moduleProgress[moduleId];
      if (!progress.lessonsCompleted.includes(lessonId)) {
        progress.lessonsCompleted.push(lessonId);
      }
      progress.currentLesson = lessonId;
      progress.lastAccessedAt = new Date();
      saveToLocalStorage(state);
    },
    completeQuiz: (
      state,
      action: PayloadAction<{ moduleId: string; quizId: string; score: number }>
    ) => {
      const { moduleId, quizId, score } = action.payload;
      if (!state.moduleProgress[moduleId]) {
        state.moduleProgress[moduleId] = {
          moduleId,
          userId: 'current-user',
          lessonsCompleted: [],
          quizzesPassed: [],
          projectsCompleted: [],
          currentLesson: '',
          startedAt: new Date(),
          lastAccessedAt: new Date(),
          score: 0,
        };
      }
      const progress = state.moduleProgress[moduleId];
      if (!progress.quizzesPassed.includes(quizId)) {
        progress.quizzesPassed.push(quizId);
      }
      progress.score = Math.max(progress.score, score);
      progress.lastAccessedAt = new Date();
      saveToLocalStorage(state);
    },
  },
});

// Helper function to save state to localStorage
const saveToLocalStorage = (state: LearningState) => {
  try {
    const toSave = {
      currentPath: state.currentPath,
      currentModule: state.currentModule,
      pathProgress: state.pathProgress,
      moduleProgress: state.moduleProgress,
      recommendedPaths: state.recommendedPaths,
    };
    localStorage.setItem('telix_learning_state', JSON.stringify(toSave));
  } catch (error) {
    console.error('Failed to save learning state:', error);
  }
};

export const {
  setCurrentPath,
  setCurrentModule,
  updatePathProgress,
  updateModuleProgress,
  setRecommendedPaths,
  completeLesson,
  completeQuiz,
} = learningSlice.actions;

export default learningSlice.reducer;

